import { wrap, wrapDelta } from "./geo";

export type BridgeType = "rift" | "river";

/** Compatible with ravine links and river polyline pieces; every length is in world cells. */
export interface BridgeSegment {
  ax: number; ay: number; bx: number; by: number;
  id?: string;
  width?: number;
  t0?: number; t1?: number;
  len?: number;
  freeA?: boolean; freeB?: boolean;
}

export interface Bridge {
  id: string;
  /** Unwrapped world position, suitable for the tile that requested it. */
  x: number; y: number;
  /** Normalised deck direction, perpendicular to the channel. */
  ux: number; uy: number;
  halfLength: number; halfWidth: number;
  type: BridgeType;
}

export interface BridgeSource {
  type: BridgeType;
  /** Return all pieces intersecting these unwrapped bounds, including complete ravine links. */
  segments: (x0: number, y0: number, x1: number, y1: number) => readonly BridgeSegment[];
  /** Half width of the actual channel at this point; river pieces can supply their own `width`. */
  gapHalfWidth?: (x: number, y: number) => number;
  chance?: number;
}

export interface BridgeOptions {
  size: number;
  seed: number;
  sources: readonly BridgeSource[];
  /** True only on solid footing outside the source's channel and any other water or rift. */
  isDry: (x: number, y: number, type: BridgeType) => boolean;
  /** Cities and lakes, including their required clearance; tested along the whole deck. */
  excluded?: (x: number, y: number) => boolean;
  chance?: number;
  minSpacing?: number;
}

interface Candidate extends Bridge { priority: number }
interface Node { x: number; y: number; directions: Map<string, [number, number]> }
const BANK_STEP = 0.035;
const MAX_HALF_LENGTH = 0.85;
const HALF_WIDTH = 0.075;
const NODE_CLEARANCE = 0.7;

function hash(id: string, seed: number): number {
  let h = (2166136261 ^ (seed | 0)) >>> 0;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

function pointKey(x: number, y: number, size: number): string {
  const n = Math.round(size * 10000);
  return `${Math.round(wrap(x, size) * 10000) % n},${Math.round(wrap(y, size) * 10000) % n}`;
}

function distance(ax: number, ay: number, bx: number, by: number, size: number): number {
  return Math.hypot(wrapDelta(ax, bx, size), wrapDelta(ay, by, size));
}

function channelNodes(segments: readonly BridgeSegment[], size: number): Map<string, Node> {
  const nodes = new Map<string, Node>();
  for (const sg of segments) {
    const a = pointKey(sg.ax, sg.ay, size), b = pointKey(sg.bx, sg.by, size);
    const dx = wrapDelta(sg.ax, sg.bx, size), dy = wrapDelta(sg.ay, sg.by, size);
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) continue;
    const add = (key: string, other: string, x: number, y: number, ux: number, uy: number) => {
      let node = nodes.get(key);
      if (!node) { node = { x, y, directions: new Map() }; nodes.set(key, node); }
      node.directions.set(other, [ux, uy]);
    };
    add(a, b, sg.ax, sg.ay, dx / len, dy / len);
    add(b, a, sg.bx, sg.by, -dx / len, -dy / len);
  }
  return nodes;
}

function unsafeNode(x: number, y: number, nodes: Map<string, Node>, size: number): boolean {
  for (const node of nodes.values()) {
    if (distance(x, y, node.x, node.y, size) > NODE_CLEARANCE) continue;
    const directions = [...node.directions.values()];
    if (directions.length !== 2) return true; // free end or junction
    const [a, b] = directions;
    if (a[0] * b[0] + a[1] * b[1] > -0.72) return true; // a bend sharper than about 44 degrees
  }
  return false;
}

function anchoredLength(x: number, y: number, ux: number, uy: number, gap: number, type: BridgeType, options: BridgeOptions): number | null {
  const dry = (along: number, across: number) => options.isDry(x + ux * along - uy * across, y + uy * along + ux * across, type);
  if (dry(0, 0)) return null;
  // Deck ends, both corner posts and a little ground beyond them must all have solid footing.
  for (let h = Math.max(0.2, gap + 0.1); h <= MAX_HALF_LENGTH; h += BANK_STEP) {
    let safe = true;
    for (const side of [-1, 1]) {
      for (const along of [h - 0.025, h + 0.055]) {
        for (const across of [-HALF_WIDTH, 0, HALF_WIDTH]) {
          if (!dry(side * along, across)) { safe = false; break; }
        }
        if (!safe) break;
      }
      if (!safe) break;
    }
    if (!safe) continue;
    if (options.excluded) {
      // Checking the deck, not just its centre, keeps a nearby lake or city out of the crossing.
      for (let along = -h; along <= h + 1e-6 && safe; along += 0.08) {
        for (const across of [-HALF_WIDTH, 0, HALF_WIDTH]) {
          if (options.excluded(x + ux * along - uy * across, y + uy * along + ux * across)) { safe = false; break; }
        }
      }
    }
    if (safe) return h;
  }
  return null;
}

/** Stable crossings with local priority arbitration. The padded query includes every rival within minSpacing, so a
 * neighbour tile cannot select a different bridge. Supply both channel sources together to space all crossings apart. */
export function findBridges(x0: number, y0: number, x1: number, y1: number, options: BridgeOptions): Bridge[] {
  const { size, seed } = options;
  const spacing = Math.max(1, options.minSpacing ?? 3);
  const pad = spacing + MAX_HALF_LENGTH + NODE_CLEARANCE + 1;
  const bx0 = x0 - pad, by0 = y0 - pad, bx1 = x1 + pad, by1 = y1 + pad;
  const candidates = new Map<string, Candidate>();
  for (const source of options.sources) {
    const segments = source.segments(bx0, by0, bx1, by1);
    const nodes = channelNodes(segments, size);
    for (const sg of segments) {
      if (source.type === "rift") {
        // One of the five pieces per logical link, away from that link's loose ends.
        if (sg.freeA || sg.freeB || sg.t0 === undefined || sg.t1 === undefined || sg.t0 > 0.5 || sg.t1 <= 0.5) continue;
      }
      const ak = pointKey(sg.ax, sg.ay, size), bk = pointKey(sg.bx, sg.by, size);
      if (ak === bk) continue;
      const forward = ak < bk;
      const ax = forward ? sg.ax : sg.bx, ay = forward ? sg.ay : sg.by;
      const bx = forward ? sg.bx : sg.ax, by = forward ? sg.by : sg.ay;
      const dx = wrapDelta(ax, bx, size), dy = wrapDelta(ay, by, size);
      const len = Math.hypot(dx, dy);
      if (len < 1e-5) continue;
      const id = `${source.type}:${sg.id ?? ""}:${forward ? ak : bk}:${forward ? bk : ak}`;
      const priority = hash(id, seed);
      if (priority / 4294967296 >= (source.chance ?? options.chance ?? 0.32)) continue;
      const t = 0.44 + (hash(id, seed ^ 0x51b3) / 4294967296) * 0.12;
      const x = ax + dx * t, y = ay + dy * t;
      if (x < bx0 + 1 || x > bx1 - 1 || y < by0 + 1 || y > by1 - 1) continue;
      if (unsafeNode(x, y, nodes, size) || options.excluded?.(x, y)) continue;
      let ux = -dy / len, uy = dx / len;
      if (ux < 0 || (Math.abs(ux) < 1e-9 && uy < 0)) { ux = -ux; uy = -uy; }
      const gap = source.gapHalfWidth?.(x, y) ?? sg.width ?? (source.type === "rift" ? 0.25 : 0.12);
      if (!(gap > 0) || gap > MAX_HALF_LENGTH - 0.1) continue;
      const halfLength = anchoredLength(x, y, ux, uy, gap, source.type, options);
      if (halfLength === null) continue;
      const candidate: Candidate = { id, x, y, ux, uy, halfLength, halfWidth: HALF_WIDTH, type: source.type, priority };
      const previous = candidates.get(id);
      // Queries spanning a wrap can include copies; keep the one nearest this request's middle.
      if (!previous || Math.hypot(x - (x0 + x1) / 2, y - (y0 + y1) / 2) < Math.hypot(previous.x - (x0 + x1) / 2, previous.y - (y0 + y1) / 2)) candidates.set(id, candidate);
    }
  }
  const all = [...candidates.values()];
  return all.filter((candidate) => {
    if (candidate.x < x0 || candidate.x > x1 || candidate.y < y0 || candidate.y > y1) return false;
    return !all.some((other) => other.id !== candidate.id && distance(candidate.x, candidate.y, other.x, other.y, size) < spacing &&
      (other.priority < candidate.priority || (other.priority === candidate.priority && other.id < candidate.id)));
  }).sort((a, b) => a.y - b.y || a.x - b.x).map(({ priority: _priority, ...bridge }) => bridge);
}

/** Weathered timber drawn in the cached ground pass; no roads, markers or hit targets. */
export function paintBridge(g: CanvasRenderingContext2D, bridge: Bridge, x0: number, top: number, s: number, alpha: number): void {
  if (alpha <= 0 || s < 20) return;
  const h = bridge.halfLength * s, w = bridge.halfWidth * s;
  const plankCount = Math.max(5, Math.ceil(bridge.halfLength * 2 / 0.085));
  const grain = hash(bridge.id, 731);
  g.save();
  g.globalAlpha = alpha;
  g.translate((bridge.x - x0) * s, (top - bridge.y) * s);
  g.rotate(Math.atan2(-bridge.uy, bridge.ux));
  // Short cast shadow and muted contacts under the two dry landings.
  g.fillStyle = "rgba(22,18,12,.27)";
  const shadowX = s * (0.028 * bridge.ux - 0.035 * bridge.uy);
  const shadowY = s * (0.028 * bridge.uy + 0.035 * bridge.ux);
  g.fillRect(-h + shadowX, -w + shadowY, h * 2, w * 2);
  g.fillStyle = "rgba(44,35,21,.32)";
  g.fillRect(-h - s * 0.025, -w - s * 0.025, s * 0.1, w * 2 + s * 0.05);
  g.fillRect(h - s * 0.075, -w - s * 0.025, s * 0.1, w * 2 + s * 0.05);
  g.fillStyle = "#51432d";
  g.fillRect(-h, -w, h * 2, w * 2);
  const step = h * 2 / plankCount;
  for (let i = 0; i < plankCount; i++) {
    const weather = ((grain >>> (i % 24)) & 7) / 7;
    g.fillStyle = `rgb(${Math.round(117 + weather * 28)},${Math.round(98 + weather * 22)},${Math.round(66 + weather * 17)})`;
    g.fillRect(-h + i * step + s * 0.005, -w, Math.max(s * 0.025, step - s * 0.009), w * 2);
    if (s >= 40 && i % 2 === 0) {
      g.strokeStyle = "rgba(56,42,24,.3)";
      g.lineWidth = Math.max(0.45, s * 0.006);
      g.beginPath(); g.moveTo(-h + i * step + step * 0.35, -w * 0.65); g.lineTo(-h + i * step + step * 0.5, w * 0.55); g.stroke();
    }
  }
  const lift = s * 0.025;
  // A local displacement (h * uy, -h * ux) always projects to screen north after the deck rotation.
  const raised = (x: number, y: number, height: number): [number, number] => [x + height * bridge.uy, y - height * bridge.ux];
  const light = -0.6 * bridge.uy - 0.8 * bridge.ux; // world light projected onto the local positive-y side
  for (const side of [-1, 1]) {
    const y = side * (w + s * 0.006);
    g.strokeStyle = side * light > 0 ? "#bdab80" : "#766143";
    g.lineWidth = Math.max(1, s * 0.019);
    g.beginPath(); g.moveTo(...raised(-h, y, lift)); g.lineTo(...raised(h, y, lift)); g.stroke();
    for (const x of [-h + s * 0.022, 0, h - s * 0.022]) {
      g.strokeStyle = "#59472e";
      g.lineWidth = Math.max(1, s * 0.022);
      g.beginPath(); g.moveTo(...raised(x, y, -s * 0.018)); g.lineTo(...raised(x, y, lift + s * 0.022)); g.stroke();
      g.fillStyle = "#baa47a";
      const [capX, capY] = raised(x, y, lift + s * 0.025);
      g.fillRect(capX - s * 0.012, capY, s * 0.024, s * 0.016);
    }
  }
  g.restore();
}
